import { createRequestId, apiBaseUrl, type ApiResult } from '@eshhanle/shared';
import { deviceName, getToken, type AppClient } from '@/auth/session';

export type { ApiResult };

export type JsonMap = Record<string, unknown>;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let activeClient: AppClient = 'customer';

export function setApiAppClient(client: AppClient): void {
  activeClient = client;
}

export function getApiAppClient(): AppClient {
  return activeClient;
}

export async function apiRequest<T = JsonMap>(
  method: 'GET' | 'POST',
  path: string,
  body?: JsonMap,
  extraHeaders: Record<string, string> = {},
): Promise<ApiResult<T> & JsonMap> {
  const requestId = createRequestId();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-App-Client': activeClient,
    'X-Request-Id': requestId,
    'X-Device-Name': deviceName(),
    ...extraHeaders,
  };

  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const base = apiBaseUrl(import.meta.env.VITE_API_BASE);
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;

  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  });

  const payload = (await response.json().catch(() => ({
    success: false,
    error: 'استجابة غير صالحة من الخادم.',
  }))) as ApiResult<T> & JsonMap;

  if (!('success' in payload)) {
    return { success: false, error: 'صيغة رد غير متوقعة.', request_id: requestId };
  }
  if (!payload.success && !payload.request_id) {
    (payload as JsonMap).request_id = requestId;
  }
  return payload;
}

export function apiGet<T = JsonMap>(path: string): Promise<ApiResult<T> & JsonMap> {
  return apiRequest<T>('GET', path);
}

export function apiPost<T = JsonMap>(
  path: string,
  body: JsonMap = {},
  extraHeaders: Record<string, string> = {},
): Promise<ApiResult<T> & JsonMap> {
  return apiRequest<T>('POST', path, body, extraHeaders);
}

/** Multipart upload (do not set Content-Type — browser sets boundary). */
export async function apiUpload<T = JsonMap>(
  path: string,
  formData: FormData,
): Promise<ApiResult<T> & JsonMap> {
  const requestId = createRequestId();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-App-Client': activeClient,
    'X-Request-Id': requestId,
    'X-Device-Name': deviceName(),
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const base = apiBaseUrl(import.meta.env.VITE_API_BASE);
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'same-origin',
  });
  const payload = (await response.json().catch(() => ({
    success: false,
    error: 'استجابة غير صالحة من الخادم.',
  }))) as ApiResult<T> & JsonMap;
  if (!('success' in payload)) {
    return { success: false, error: 'صيغة رد غير متوقعة.', request_id: requestId };
  }
  if (!payload.success && !payload.request_id) {
    (payload as JsonMap).request_id = requestId;
  }
  return payload;
}

export function asList(value: unknown): JsonMap[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is JsonMap => !!item && typeof item === 'object');
  }
  return [];
}

export function pickList(payload: JsonMap, ...keys: string[]): JsonMap[] {
  for (const key of keys) {
    if (!(key in payload)) continue;
    // Only accept real arrays at the top level — `{ data: { products: [] } }` must fall through.
    if (Array.isArray(payload[key])) {
      return asList(payload[key]);
    }
  }
  if (payload.data && typeof payload.data === 'object') {
    if (Array.isArray(payload.data)) {
      return asList(payload.data);
    }
    const data = payload.data as JsonMap;
    for (const key of keys) {
      if (key === 'data') continue;
      if (key in data) return asList(data[key]);
    }
  }
  return [];
}

export function errorMessage(payload: JsonMap, fallback = 'حدث خطأ غير متوقع.'): string {
  if (typeof payload.error === 'string' && payload.error.trim()) return payload.error;
  if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;
  return fallback;
}
